import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Calendar } from 'primereact/calendar'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { Divider } from 'primereact/divider'
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { DateTemplate } from '../../../../../../components/DateTemplate'
import { InputNumberToggleable } from '../../../../../../components/inputs'
import { Invoice, InvoiceItem } from '../../../../../../data/models'
import { delay } from '../../../../../../utils/helper/delay'
import { calculateItemPrice } from '../../../../../../utils/price-calculation'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config/context'

interface FormProps {
  currency: Invoice['OrderCurrency']
  formik: FormikProps<any>
  isFormFieldValid: any
  getFormErrorMessage: any
  invoiceItem: InvoiceItem
  setIsRequestRunning: React.Dispatch<React.SetStateAction<boolean>>
  isLoading: boolean
}

export const EditForm: React.FC<FormProps> = ({
  currency,
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  invoiceItem,
  setIsRequestRunning,
  isLoading,
}) => {
  const { i18n } = useTranslation()
  const { language } = i18n

  const { uiConfig: config } = useConfigContext()

  const [isCalculatingPriceSum, setIsCalculatingPriceSum] = useState(false)
  const [isCalculatingLineItemAmount, setIsCalculatingLineItemAmount] =
    useState(false)

  const calculateConfirmationPriceSum = async (
    confirmationQuantity: number,
    confirmationPriceBaseQuantity: number,
    confirmationPrice: number,
    force: boolean = false,
  ) => {
    if (!formik.values.confirmationPriceSumChecked || force) {
      setIsCalculatingPriceSum(true)
      setIsRequestRunning(true)

      const priceSum = calculateItemPrice(
        confirmationQuantity,
        confirmationPriceBaseQuantity,
        confirmationPrice,
      )

      await formik.setFieldValue('confirmationPriceSum', priceSum)
      await delay(1)

      setIsCalculatingPriceSum(false)
      setIsRequestRunning(false)

      return priceSum
    }
  }

  const calculateConfirmationLineItemAmount = async (
    priceSum: number,
    allowanceChargeSum: number,
    force: boolean = false,
  ) => {
    if (!formik.values.confirmationLineItemAmountChecked || force) {
      setIsCalculatingLineItemAmount(true)
      setIsRequestRunning(true)

      // We multiply by 10 and divide by 10 to improve precision and
      // reduce the number of decimal places in the sum because
      // JavaScript has problems with math.
      const sum = (priceSum * 10 + allowanceChargeSum * 10) / 10

      await formik.setFieldValue('confirmationLineItemAmount', sum)
      await delay(1)

      setIsCalculatingLineItemAmount(false)
      setIsRequestRunning(false)
    }
  }

  const onConfirmationQuantityChange = async (e: InputNumberChangeEvent) => {
    const confirmationQuantity = (e.value as number) || 0
    const confirmationPriceBaseQuantity =
      (formik.values.confirmationPriceBaseQuantity as number) || 0
    const confirmationPrice = (formik.values.confirmationPrice as number) || 0
    const confirmationAllowanceChargeSum =
      (formik.values.confirmationAllowanceChargeSum as number) || 0

    await formik.setFieldValue('confirmationQuantity', confirmationQuantity)

    const priceSum =
      ((await calculateConfirmationPriceSum(
        confirmationQuantity,
        confirmationPriceBaseQuantity,
        confirmationPrice,
      )) as number) || 0

    await calculateConfirmationLineItemAmount(
      priceSum,
      confirmationAllowanceChargeSum,
    )
  }

  const onConfirmationPriceChange = async (e: InputNumberChangeEvent) => {
    const confirmationPrice = (e.value as number) || 0
    const confirmationPriceBaseQuantity =
      (formik.values.confirmationPriceBaseQuantity as number) || 0
    const confirmationQuantity =
      (formik.values.confirmationQuantity as number) || 0
    const confirmationAllowanceChargeSum =
      (formik.values.confirmationAllowanceChargeSum as number) || 0

    await formik.setFieldValue('confirmationPrice', confirmationPrice)

    const priceSum =
      ((await calculateConfirmationPriceSum(
        confirmationQuantity,
        confirmationPriceBaseQuantity,
        confirmationPrice,
      )) as number) || 0

    await calculateConfirmationLineItemAmount(
      priceSum,
      confirmationAllowanceChargeSum,
    )
  }

  const onConfirmationAllowanceChargeSumChange = async (
    e: InputNumberChangeEvent,
  ) => {
    const allowanceChargeSum = (e.value as number) || 0
    const priceSum = (formik.values.confirmationPriceSum as number) || 0

    await formik.setFieldValue(
      'confirmationAllowanceChargeSum',
      allowanceChargeSum,
    )

    await calculateConfirmationLineItemAmount(priceSum, allowanceChargeSum)
  }

  const onConfirmationPriceSumChange = async (e: InputNumberChangeEvent) => {
    const priceSum = (e.value as number) || 0
    const allowanceChargeSum =
      (formik.values.confirmationAllowanceChargeSum as number) || 0
    await formik.setFieldValue('confirmationPriceSum', priceSum)
    await calculateConfirmationLineItemAmount(priceSum, allowanceChargeSum)
  }

  const onConfirmationPriceSumCheckedChange = async (
    e: CheckboxChangeEvent,
  ) => {
    await formik.setFieldValue('confirmationPriceSumChecked', e.checked)

    if (!e.checked) {
      const price = (formik.values.confirmationPrice as number) || 0
      const quantity = (formik.values.confirmationQuantity as number) || 0
      const baseQuantity =
        (formik.values.confirmationPriceBaseQuantity as number) || 0
      const allowanceChargeSum =
        (formik.values.confirmationAllowanceChargeSum as number) || 0

      const priceSum =
        ((await calculateConfirmationPriceSum(
          quantity,
          baseQuantity,
          price,
          true,
        )) as number) || 0

      await calculateConfirmationLineItemAmount(priceSum, allowanceChargeSum)
    }
  }

  const onConfirmationLineItemAmountCheckedChange = async (
    e: CheckboxChangeEvent,
  ) => {
    await formik.setFieldValue('confirmationLineItemAmountChecked', e.checked)

    if (!e.checked) {
      const allowanceChargeSum =
        (formik.values.confirmationAllowanceChargeSum as number) || 0
      const priceSum = (formik.values.confirmationPriceSum as number) || 0

      await calculateConfirmationLineItemAmount(
        priceSum,
        allowanceChargeSum,
        true,
      )
    }
  }

  // itemId
  const itemIdLabel = <label htmlFor="itemId" className="text-sm"></label>

  const itemIdComponent = (
    <InputText
      id="itemId"
      className="w-full"
      value={invoiceItem?.ItemId}
      disabled
    />
  )

  const itemIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={itemIdComponent}
      label={itemIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationItemId
  const confirmationItemIdLabel = (
    <label
      htmlFor="confirmationItemId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationItemId'),
      })}
    ></label>
  )

  const confirmationItemIdComponent = (
    <InputText
      id="confirmationItemId"
      value={formik.values.confirmationItemId || ''}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationItemId'),
      })}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationItemIdErrorMsg = (
    <div>{getFormErrorMessage('confirmationItemId')}</div>
  )

  const confirmationItemIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationItemIdComponent}
      label={confirmationItemIdLabel}
      errorMsg={confirmationItemIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // countryOfOrigin
  const countryOfOriginLabel = (
    <label htmlFor="countryOfOrigin" className="text-sm"></label>
  )

  const countryOfOriginComponent = (
    <InputText
      id="countryOfOrigin"
      className="w-full"
      value={invoiceItem?.CountryOfOrigin}
      disabled
    />
  )

  const countryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={countryOfOriginComponent}
      label={countryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // confirmationCountryOfOrigin
  const confirmationCountryOfOriginLabel = (
    <label
      htmlFor="confirmationCountryOfOrigin"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
    ></label>
  )

  const confirmationCountryOfOriginComponent = (
    <InputText
      id="confirmationCountryOfOrigin"
      value={formik.values.confirmationCountryOfOrigin || ''}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
      keyfilter="alpha"
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationCountryOfOriginErrorMsg = (
    <div>{getFormErrorMessage('confirmationCountryOfOrigin')}</div>
  )

  const confirmationCountryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationCountryOfOriginComponent}
      label={confirmationCountryOfOriginLabel}
      errorMsg={confirmationCountryOfOriginErrorMsg}
      isLoading={isLoading}
    />
  )

  // gtin
  const gtinLabel = <label htmlFor="gtin" className="text-sm"></label>

  const gtinComponent = (
    <InputText
      id="gtin"
      className="w-full"
      value={invoiceItem?.GTIN}
      disabled
    />
  )

  const gtinWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={gtinComponent}
      label={gtinLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={invoiceItem?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductId
  const confirmationSellerProductIdLabel = (
    <label
      htmlFor="confirmationSellerProductId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationSellerProductId'),
      })}
    ></label>
  )

  const confirmationSellerProductIdComponent = (
    <InputText
      id="confirmationSellerProductId"
      value={formik.values.confirmationSellerProductId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationSellerProductId'),
      })}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationSellerProductIdErrorMsg = (
    <div>{getFormErrorMessage('confirmationSellerProductId')}</div>
  )

  const confirmationSellerProductIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationSellerProductIdComponent}
      label={confirmationSellerProductIdLabel}
      errorMsg={confirmationSellerProductIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={invoiceItem?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductDescription
  const confirmationSellerProductDescriptionLabel = (
    <label
      htmlFor="confirmationSellerProductDescription"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationSellerProductDescription'),
      })}
    ></label>
  )

  const confirmationSellerProductDescriptionComponent = (
    <InputText
      id="confirmationSellerProductDescription"
      value={formik.values.confirmationSellerProductDescription}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationSellerProductDescription'),
      })}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationSellerProductDescriptionErrorMsg = (
    <div>{getFormErrorMessage('confirmationSellerProductDescription')}</div>
  )

  const confirmationSellerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationSellerProductDescriptionComponent}
      label={confirmationSellerProductDescriptionLabel}
      errorMsg={confirmationSellerProductDescriptionErrorMsg}
      isLoading={isLoading}
    />
  )

  //  quantity
  const quantityLabel = <label htmlFor="quantity" className="text-sm"></label>

  const quantityComponent = (
    <InputNumber
      id="quantity"
      className="w-full"
      value={invoiceItem?.Quantity}
      locale={language}
      disabled
    />
  )

  const quantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={quantityComponent}
      label={quantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationQuantity
  const confirmationQuantityLabel = (
    <label
      htmlFor="confirmationQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationQuantity'),
      })}
    ></label>
  )

  const confirmationQuantityComponent = (
    <InputNumber
      id="confirmationQuantity"
      value={formik.values.confirmationQuantity}
      onChange={onConfirmationQuantityChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationQuantity'),
      })}
      allowEmpty={false}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
      locale={language}
    />
  )

  const confirmationQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationQuantity')}</div>
  )

  const confirmationQuantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationQuantityComponent}
      label={confirmationQuantityLabel}
      errorMsg={confirmationQuantityErrorMsg}
      isLoading={isLoading}
    />
  )

  // unit
  const unitLabel = <label htmlFor="unit" className="text-sm"></label>

  const unitComponent = (
    <InputText
      id="unit"
      className="w-full"
      value={invoiceItem?.Unit}
      disabled
    />
  )

  const unitWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={unitComponent}
      label={unitLabel}
      isLoading={isLoading}
    />
  )

  // confirmationUnit
  const confirmationUnitLabel = (
    <label
      htmlFor="confirmationUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationUnit'),
      })}
    ></label>
  )

  const confirmationUnitComponent = (
    <InputText
      id="confirmationUnit"
      value={formik.values.confirmationUnit}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationUnit'),
      })}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationUnitErrorMsg = (
    <div>{getFormErrorMessage('confirmationUnit')}</div>
  )

  const confirmationUnitWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationUnitComponent}
      label={confirmationUnitLabel}
      errorMsg={confirmationUnitErrorMsg}
      isLoading={isLoading}
    />
  )

  // priceBaseQuantity
  const priceBaseQuantityLabel = (
    <label htmlFor="priceBaseQuantity" className="text-sm"></label>
  )

  const priceBaseQuantityComponent = (
    <InputNumber
      id="priceBaseQuantity"
      className="w-full"
      value={invoiceItem?.PriceBaseQuantity}
      locale={language}
      disabled
    />
  )

  const priceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={priceBaseQuantityComponent}
      label={priceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceBaseQuantity
  const confirmationPriceBaseQuantityLabel = (
    <label
      htmlFor="confirmationPriceBaseQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceBaseQuantity'),
      })}
    ></label>
  )

  const confirmationPriceBaseQuantityComponent = (
    <InputNumber
      id="confirmationPriceBaseQuantity"
      value={formik.values.confirmationPriceBaseQuantity}
      onChange={e =>
        formik.setFieldValue('confirmationPriceBaseQuantity', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceBaseQuantity'),
      })}
      locale={language}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationPriceBaseQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceBaseQuantity')}</div>
  )

  const confirmationPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationPriceBaseQuantityComponent}
      label={confirmationPriceBaseQuantityLabel}
      errorMsg={confirmationPriceBaseQuantityErrorMsg}
      isLoading={isLoading}
    />
  )

  // priceUnitOfMeasure
  const priceUnitOfMeasureLabel = (
    <label htmlFor="priceUnitOfMeasure" className="text-sm"></label>
  )

  const priceUnitOfMeasureComponent = (
    <InputText
      id="priceUnitOfMeasure"
      className="w-full"
      value={invoiceItem?.PriceUnitOfMeasure}
      disabled
    />
  )

  const priceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={priceUnitOfMeasureComponent}
      label={priceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceUnitOfMeasure
  const confirmationPriceUnitOfMeasureLabel = (
    <label
      htmlFor="confirmationPriceUnitOfMeasure"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceUnitOfMeasure'),
      })}
    ></label>
  )

  const confirmationPriceUnitOfMeasureComponent = (
    <InputText
      id="confirmationPriceUnitOfMeasure"
      value={formik.values.confirmationPriceUnitOfMeasure?.toUpperCase()}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceUnitOfMeasure'),
      })}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationPriceUnitOfMeasureErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceUnitOfMeasure')}</div>
  )

  const confirmationPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationPriceUnitOfMeasureComponent}
      label={confirmationPriceUnitOfMeasureLabel}
      errorMsg={confirmationPriceUnitOfMeasureErrorMsg}
      isLoading={isLoading}
    />
  )

  // price
  const priceLabel = <label htmlFor="price" className="text-sm"></label>
  const priceComponent = (
    <InputNumber
      id="price"
      value={invoiceItem?.Price}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={priceComponent}
      label={priceLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPrice
  const confirmationPriceLabel = (
    <label
      htmlFor="confirmationPrice"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPrice'),
      })}
    ></label>
  )

  const confirmationPriceComponent = (
    <InputNumber
      id="confirmationPrice"
      value={formik.values.confirmationPrice}
      onChange={onConfirmationPriceChange}
      mode="currency"
      currency={currency}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPrice'),
      })}
      allowEmpty={false}
      locale={language}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationPriceErrorMsg = (
    <div>{getFormErrorMessage('confirmationPrice')}</div>
  )

  const confirmationPriceWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationPriceComponent}
      label={confirmationPriceLabel}
      errorMsg={confirmationPriceErrorMsg}
      isLoading={isLoading}
    />
  )

  // priceSum
  const priceSumLabel = <label htmlFor="priceSum" className="text-sm"></label>

  const priceSumComponent = (
    <InputNumber
      id="priceSum"
      value={invoiceItem?.PriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={priceSumComponent}
      label={priceSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceSum
  const confirmationPriceSumLabel = (
    <label
      htmlFor="confirmationPriceSum"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceSum'),
      })}
    ></label>
  )

  const confirmationPriceSumComponent = (
    <InputNumberToggleable
      id="confirmationPriceSum"
      checked={formik.values.confirmationPriceSumChecked}
      onCheckedChange={onConfirmationPriceSumCheckedChange}
      mode="currency"
      currency={currency}
      value={formik.values.confirmationPriceSum}
      onChange={onConfirmationPriceSumChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceSum'),
      })}
      allowEmpty={false}
      locale={language}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationPriceSumErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceSum')}</div>
  )

  const confirmationPriceSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationPriceSumComponent}
      label={confirmationPriceSumLabel}
      errorMsg={confirmationPriceSumErrorMsg}
      isLoading={isLoading}
      isCalculating={isCalculatingPriceSum}
    />
  )

  // allowanceChargeSum
  const allowanceChargeSumLabel = (
    <label htmlFor="allowanceChargeSum" className="text-sm"></label>
  )

  const allowanceChargeSumComponent = (
    <InputNumber
      id="allowanceChargeSum"
      value={invoiceItem?.AllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const allowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={allowanceChargeSumComponent}
      label={allowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAllowanceChargeSum
  const confirmationAllowanceChargeSumLabel = (
    <label
      htmlFor="confirmationAllowanceChargeSum"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationAllowanceChargeSum'),
      })}
    ></label>
  )

  const confirmationAllowanceChargeSumComponent = (
    <InputNumber
      id="confirmationAllowanceChargeSum"
      mode="currency"
      currency={currency}
      value={formik.values.confirmationAllowanceChargeSum}
      onChange={onConfirmationAllowanceChargeSumChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationAllowanceChargeSum'),
      })}
      allowEmpty={false}
      locale={language}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationAllowanceChargeSumErrorMsg = (
    <div>{getFormErrorMessage('confirmationAllowanceChargeSum')}</div>
  )

  const confirmationAllowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationAllowanceChargeSumComponent}
      label={confirmationAllowanceChargeSumLabel}
      errorMsg={confirmationAllowanceChargeSumErrorMsg}
      isLoading={isLoading}
    />
  )

  // lineItemAmount
  const lineItemAmountLabel = (
    <label htmlFor="lineItemAmount" className="text-sm"></label>
  )

  const lineItemAmountComponent = (
    <InputNumber
      id="lineItemAmount"
      value={invoiceItem?.LineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const lineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={lineItemAmountComponent}
      label={lineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationLineItemAmount
  const confirmationLineItemAmountLabel = (
    <label
      htmlFor="confirmationLineItemAmount"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationLineItemAmount'),
      })}
    ></label>
  )

  const confirmationLineItemAmountComponent = (
    <InputNumberToggleable
      id="confirmationLineItemAmount"
      checked={formik.values.confirmationLineItemAmountChecked}
      onCheckedChange={onConfirmationLineItemAmountCheckedChange}
      mode="currency"
      currency={currency}
      value={formik.values.confirmationLineItemAmount}
      onChange={e =>
        formik.setFieldValue('confirmationLineItemAmount', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationLineItemAmount'),
      })}
      allowEmpty={false}
      locale={language}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationLineItemAmountErrorMsg = (
    <div>{getFormErrorMessage('confirmationLineItemAmount')}</div>
  )

  const confirmationLineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationLineItemAmountComponent}
      label={confirmationLineItemAmountLabel}
      errorMsg={confirmationLineItemAmountErrorMsg}
      isLoading={isLoading}
      isCalculating={isCalculatingLineItemAmount}
    />
  )

  // deliveryDate
  const deliveryDateLabel = (
    <label htmlFor="deliveryDate" className="text-sm"></label>
  )

  const deliveryDate = invoiceItem?.DeliveryDate
    ? new Date(invoiceItem?.DeliveryDate)
    : null

  const deliveryDateComponent = (
    <Calendar
      id="deliveryDate"
      value={deliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label
      htmlFor="confirmationDeliveryDate"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryDate'),
      })}
    ></label>
  )

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={formik.values.confirmationDeliveryDate}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryDate'),
      })}
      showButtonBar
      showIcon
      minDate={new Date()}
      dateTemplate={(date: any) => (
        <DateTemplate
          date={date}
          desiredDeliveryDate={invoiceItem?.DeliveryDate}
        />
      )}
      locale={language}
      disabled={invoiceItem?.ConfirmationCode === 'REJECTED'}
    />
  )

  const confirmationDeliveryDateErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryDate')}</div>
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.editInvoiceItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      errorMsg={confirmationDeliveryDateErrorMsg}
      isLoading={isLoading}
    />
  )

  return (
    <>
      {/* itemId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{itemIdWrapper}</div>
        <div className="col-12 md:col-6">{confirmationItemIdWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* countryOfOrigin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{countryOfOriginWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationCountryOfOriginWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* gtin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{gtinWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductIdWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductIdWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductDescription */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductDescriptionWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* quantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{quantityWrapper}</div>

        <div className="col-12 md:col-6">{confirmationQuantityWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* unit */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{unitWrapper}</div>

        <div className="col-12 md:col-6">{confirmationUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* priceBaseQuantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceBaseQuantityWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceBaseQuantityWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* priceUnitOfMeasure */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceUnitOfMeasureWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceUnitOfMeasureWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* price */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* confirmationPriceSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceSumWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceSumWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* allowanceChargeSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{allowanceChargeSumWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationAllowanceChargeSumWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* ItemAmount */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{lineItemAmountWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationLineItemAmountWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* deliveryDate */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{deliveryDateWrapper}</div>

        <div className="col-12 md:col-6">{confirmationDeliveryDateWrapper}</div>
      </div>
    </>
  )
}
